
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { debugLog, debugError } from '@/config/debug';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
}

interface RegisterFormProps {
  onBack: () => void;
  onTerms: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onBack, onTerms }) => {
  const { t } = useTranslation();
  const { register: registerUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRegisterConfirmationDialogOpen, setIsRegisterConfirmationDialogOpen] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    defaultValues: {
      acceptTerms: false,
    },
  });

  const watchPassword = watch('password');
  const watchAcceptTerms = watch('acceptTerms');

  const onSubmit = async (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      setError(t('passwords_do_not_match'));
      return;
    }

    if (!data.acceptTerms) {
      setError(t('terms_acceptance_required'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      debugLog('HOOK', '🔄 Starting user registration...');
      debugLog('HOOK', '📧 Email:', data.email);
      debugLog('HOOK', '👤 First Name:', data.firstName);
      debugLog('HOOK', '👤 Last Name:', data.lastName);
      
      const result = await registerUser(data.email, data.password, data.firstName, data.lastName);
      
      debugLog('HOOK', '✅ Registration result:', result);
      
      if (result.needsConfirmation) {
        debugLog('HOOK', '📨 Email confirmation required');
        setIsRegisterConfirmationDialogOpen(true);
      } else {
        debugLog('HOOK', '🚀 User registered and logged in automatically');
      }
    } catch (error: any) {
      debugError('HOOK', '❌ Registration error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-2xl font-bold text-foreground">{t('create_user')}</h2>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('name')}</Label>
              <Input
                id="firstName"
                placeholder={t('name')}
                {...register('firstName', {
                  required: t('first_name_required'),
                })}
                className={errors.firstName ? 'border-destructive' : ''}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">{t('last_name')}</Label>
              <Input
                id="lastName"
                placeholder={t('last_name')}
                {...register('lastName', {
                  required: t('last_name_required'),
                })}
                className={errors.lastName ? 'border-destructive' : ''}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('email_title')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('email_placeholder')}
              {...register('email', {
                required: t('required'),
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: t('email_invalid'),
                },
              })}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('password_title')}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('password_placeholder')}
                {...register('password', {
                  required: t('required'),
                  minLength: {
                    value: 6,
                    message: t('password_min_length'),
                  },
                })}
                className={`pr-10 ${errors.password ? 'border-destructive' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">{t('confirm_password')}</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('confirm_password_placeholder')}
                {...register('confirmPassword', {
                  required: t('required'),
                  validate: (value) =>
                    value === watchPassword || t('passwords_do_not_match'),
                })}
                className={`pr-10 ${errors.confirmPassword ? 'border-destructive' : ''}`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-start space-x-2">
              <Checkbox
                id="acceptTerms"
                checked={watchAcceptTerms}
                onCheckedChange={(checked) => setValue('acceptTerms', !!checked)}
                {...register('acceptTerms', {
                  required: t('terms_acceptance_required'),
                })}
                className={errors.acceptTerms ? 'border-destructive' : ''}
              />
              <Label htmlFor="acceptTerms" className="text-sm leading-5">
                {t('accept_terms')}{' '}
                <button
                  type="button"
                  onClick={onTerms}
                  className="text-primary underline"
                >
                  {t('accept_terms_1')}
                </button>
                {' '}*
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-destructive">{errors.acceptTerms.message}</p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || !watchAcceptTerms}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('create_user')}
          </Button>
        </form>
      </div>

      <Dialog open={isRegisterConfirmationDialogOpen} onOpenChange={setIsRegisterConfirmationDialogOpen}>
        <DialogContent className="sm:max-w-[500px] w-full">
          <DialogHeader>  
            <DialogTitle className="text-2xl title-green">
              {t('registration_complete')}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>
            {t('registration_complete_success')}
          </DialogDescription>
          <DialogFooter>
            <Button onClick={() => {
              setIsRegisterConfirmationDialogOpen(false);
              onBack(); // Return to login
            }}>
              {t('close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
